/* import __COLOCATED_TEMPLATE__ from './hitl-card.hbs'; */
/* RESPONSIBLE TEAM: team-inbox */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import type { HitlCardState } from 'embercom/objects/inbox/conversation';
import type Session from 'embercom/services/session';
import type Snackbar from 'embercom/services/snackbar';
import type IntlService from 'embercom/services/intl';
import { post } from 'embercom/lib/ajax';

interface Signature {
  Element: HTMLDivElement;
  Args: {
    hitlCardState?: HitlCardState;
    conversationId?: number;
  };
}

export default class HitlCard extends Component<Signature> {
  @service declare session: Session;
  @service declare snackbar: Snackbar;
  @service declare intl: IntlService;

  @tracked attributeValues: string[] = [];
  @tracked isSubmitting = false;

  get expiresAtDate(): Date | null {
    let expiresAt = this.args.hitlCardState?.expiresAt;
    return expiresAt ? new Date(expiresAt) : null;
  }

  get formattedTimeRemaining(): string {
    if (!this.expiresAtDate) {
      return '';
    }
    let diffMs = this.expiresAtDate.getTime() - Date.now();
    let diffSeconds = Math.round(Math.abs(diffMs) / 1000);

    if (diffSeconds < 60) {
      return `${diffSeconds}s`;
    }
    let minutes = Math.round(diffSeconds / 60);
    if (minutes < 60) {
      return `${minutes}m`;
    }
    let hours = Math.round(minutes / 60);
    if (hours < 24) {
      return `${hours}h`;
    }
    let days = Math.round(hours / 24);
    return `${days}d`;
  }

  get canSubmit(): boolean {
    return (
      this.args.hitlCardState?.hitlAttributesToCollect.every(
        (_, index) => !!this.attributeValues[index],
      ) ?? false
    );
  }

  @action updateAttributeValue(index: number, value: string) {
    let newValues = [...this.attributeValues];
    newValues[index] = value;
    this.attributeValues = newValues;
  }

  @action stopPropagation(event: Event) {
    event.stopPropagation();
  }

  @action async submit() {
    this.isSubmitting = true;
    try {
      await post(`/ember/conversations/${this.args.conversationId}/submit_hitl_response`, {
        app_id: this.session.workspace.id,
        attribute_values: (this.args.hitlCardState?.hitlAttributesToCollect ?? []).map(
          (attr, index) => ({
            identifier: attr.identifier,
            value: this.attributeValues[index],
          }),
        ),
      });
    } catch (e: unknown) {
      if (typeof e === 'object' && e !== null && 'jqXHR' in e) {
        let jqError = e as { jqXHR?: { responseJSON?: { errors?: string[] } } };
        let serverError = jqError.jqXHR?.responseJSON?.errors?.[0];
        this.snackbar.notifyError(
          serverError ?? this.intl.t('inbox.composer.hitl-card.submit-error'),
        );
      } else {
        this.snackbar.notifyError(this.intl.t('inbox.composer.hitl-card.submit-error'));
      }
    } finally {
      this.isSubmitting = false;
    }
  }

  @action async takeOver() {
    this.isSubmitting = true;
    try {
      await post(`/ember/conversations/${this.args.conversationId}/take_over_hitl`, {
        app_id: this.session.workspace.id,
      });
    } catch {
      this.snackbar.notifyError(this.intl.t('inbox.composer.hitl-card.take-over-error'));
    } finally {
      this.isSubmitting = false;
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Inbox2::Composer::HitlCard': typeof HitlCard;
    'inbox2/composer/hitl-card': typeof HitlCard;
  }
}
